import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { PickupSpot } from '@/physics/pickups';

/**
 * PickupsView.ts — the floating item boxes (M12): a spinning, glowing glass cube
 * with a "?" sprite inside, Mario-Kart style. Purely cosmetic — it draws the
 * race's PickupSpot state (present / respawning) and never feeds back into it.
 *
 * GS4 redesign: at close range the bare translucent cube collapsed into a flat
 * 2D diamond (opacity-0.5 faces, no edge definition). The cube now carries a
 * merged 12-edge EMISSIVE FRAME (one geometry, one draw) that keeps the box
 * silhouette crisp from every angle, plus a faint BackSide additive SHELL for
 * a fresnel-ish volume glow, and the "?" went brand-gold. All geometry +
 * materials are shared across boxes; collected boxes still shrink away and pop
 * back on respawn, spinning in place and bobbing on a per-box phase. Reads on
 * Low quality — the frame/shell glow is real colour, not bloom.
 */

const BOX_SIZE = 1.8;
const EDGE_T = 0.1; // edge-frame bar thickness
const BOB_AMPLITUDE = 0.22;
const SHOW_RATE = 7; // how fast a respawned box scales back in
const HIDE_RATE = 14; // how fast a collected box vanishes

function questionTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.font = 'bold 96px system-ui, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineWidth = 14;
    g.strokeStyle = 'rgba(52, 34, 6, 0.92)';
    g.strokeText('?', 64, 70);
    // Brand-gold "?" with a warm vertical shine.
    const grad = g.createLinearGradient(0, 16, 0, 116);
    grad.addColorStop(0, '#ffe9a0');
    grad.addColorStop(0.45, '#ffd21c');
    grad.addColorStop(1, '#e8a20e');
    g.fillStyle = grad;
    g.fillText('?', 64, 70);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 2;
  return tex;
}

/** A soft radial halo (shared by every box's glow sprite). */
function haloTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    const grad = g.createRadialGradient(32, 32, 2, 32, 32, 31);
    grad.addColorStop(0, 'rgba(140, 220, 255, 0.85)');
    grad.addColorStop(0.45, 'rgba(90, 180, 255, 0.30)');
    grad.addColorStop(1, 'rgba(60, 140, 255, 0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
  }
  return new THREE.CanvasTexture(c);
}

/** The 12 cube edges as ONE merged geometry (one draw call per box). */
function edgeFrameGeometry(size: number, t: number): THREE.BufferGeometry {
  const h = size / 2;
  const parts: THREE.BufferGeometry[] = [];
  const bar = (w: number, hh: number, d: number, x: number, y: number, z: number): void => {
    const g = new THREE.BoxGeometry(w, hh, d);
    g.translate(x, y, z);
    parts.push(g);
  };
  const len = size + t; // overrun so corners close
  for (const a of [-h, h])
    for (const b of [-h, h]) {
      bar(len, t, t, 0, a, b); // 4 along X
      bar(t, len, t, a, 0, b); // 4 along Y
      bar(t, t, len, a, b, 0); // 4 along Z
    }
  const merged = mergeGeometries(parts);
  for (const p of parts) p.dispose();
  return merged;
}

interface BoxEntry {
  group: THREE.Group;
  cube: THREE.Mesh;
  halo: THREE.Sprite;
  baseY: number;
  phase: number;
  scale: number;
}

export class PickupsView {
  readonly group = new THREE.Group();

  private cubeGeo = new THREE.BoxGeometry(BOX_SIZE, BOX_SIZE, BOX_SIZE);
  private cubeMat = new THREE.MeshStandardMaterial({
    color: 0x7fd8ff,
    emissive: 0x1c64b8,
    emissiveIntensity: 0.95,
    roughness: 0.15,
    metalness: 0.55,
    transparent: true,
    opacity: 0.42,
    depthWrite: false, // translucent: don't punch holes in what's behind
  });
  // GS4: crisp glowing edges — the box reads as a 3D object at point-blank.
  private frameGeo = edgeFrameGeometry(BOX_SIZE, EDGE_T);
  private frameMat = new THREE.MeshBasicMaterial({
    color: 0xbfeaff,
    transparent: true,
    opacity: 0.95,
  });
  // Faint additive BackSide shell — a fresnel-ish inner glow with volume.
  private shellGeo = new THREE.BoxGeometry(BOX_SIZE * 1.16, BOX_SIZE * 1.16, BOX_SIZE * 1.16);
  private shellMat = new THREE.MeshBasicMaterial({
    color: 0x4aa8ff,
    transparent: true,
    opacity: 0.16,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
    depthWrite: false,
  });
  private qTexture = questionTexture();
  private qMat = new THREE.SpriteMaterial({ map: this.qTexture, transparent: true });
  private haloTex = haloTexture();
  private haloMat = new THREE.SpriteMaterial({
    map: this.haloTex,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  private boxes: BoxEntry[] = [];
  private t = 0;

  /** (Re)build the box meshes for a new track's spots. */
  setSpots(spots: PickupSpot[]): void {
    for (const b of this.boxes) this.group.remove(b.group);
    this.boxes = [];
    for (let i = 0; i < spots.length; i++) {
      const s = spots[i];
      const g = new THREE.Group();
      g.position.set(s.x, s.y, s.z);
      const cube = new THREE.Mesh(this.cubeGeo, this.cubeMat);
      // Frame + shell ride the cube so they tumble together.
      const frame = new THREE.Mesh(this.frameGeo, this.frameMat);
      frame.renderOrder = 2;
      const shell = new THREE.Mesh(this.shellGeo, this.shellMat);
      shell.renderOrder = 1;
      cube.add(frame, shell);
      const q = new THREE.Sprite(this.qMat);
      q.scale.set(1.15, 1.15, 1);
      q.renderOrder = 3;
      const halo = new THREE.Sprite(this.haloMat);
      halo.scale.set(4.6, 4.6, 1);
      g.add(halo, cube, q);
      this.group.add(g);
      this.boxes.push({ group: g, cube, halo, baseY: s.y, phase: i * 1.7, scale: 1 });
    }
  }

  /** Spin + bob the boxes, breathe the glow, ease in/out with the race state. */
  update(dt: number, spots: PickupSpot[]): void {
    this.t += dt;
    // One shared emissive pulse (M14 owner #8): the boxes visibly breathe.
    const breathe = Math.sin(this.t * 2.6);
    this.cubeMat.emissiveIntensity = 1.05 + breathe * 0.4;
    this.frameMat.opacity = 0.86 + breathe * 0.14;
    for (let i = 0; i < this.boxes.length; i++) {
      const b = this.boxes[i];
      const present = spots[i].respawn <= 0;
      const target = present ? 1 : 0;
      const rate = target > b.scale ? SHOW_RATE : HIDE_RATE;
      b.scale += (target - b.scale) * Math.min(rate * dt, 1);
      const visible = b.scale > 0.02;
      b.group.visible = visible;
      if (!visible) continue;
      b.group.scale.setScalar(b.scale);
      b.group.position.y = b.baseY + Math.sin(this.t * 2.1 + b.phase) * BOB_AMPLITUDE;
      b.cube.rotation.y = this.t * 1.5 + b.phase;
      b.cube.rotation.x = this.t * 0.8 + b.phase * 0.4;
      // Per-box halo pulse, slightly out of phase so rows shimmer.
      const pulse = 1 + Math.sin(this.t * 2.6 + b.phase) * 0.18;
      b.halo.scale.set(4.6 * pulse, 4.6 * pulse, 1);
    }
  }

  dispose(): void {
    this.cubeGeo.dispose();
    this.cubeMat.dispose();
    this.frameGeo.dispose();
    this.frameMat.dispose();
    this.shellGeo.dispose();
    this.shellMat.dispose();
    this.qMat.dispose();
    this.qTexture.dispose();
    this.haloMat.dispose();
    this.haloTex.dispose();
  }
}
